/**
 * Clerk import shim with an env-gated test-auth mode (#358, Item A).
 *
 * Every component that previously imported from '@clerk/clerk-react' imports
 * from here instead. With REACT_APP_TEST_MODE unset/false — every normal
 * build — each export below IS the corresponding @clerk/clerk-react binding,
 * so behavior is unchanged. TEST_MODE is a build-time constant (see
 * ../testMode.js): in normal builds the mock branch is dead code.
 *
 * With REACT_APP_TEST_MODE=true (local test builds only), Clerk is bypassed:
 * the app renders as an already-authenticated test user and api/client.js
 * attaches X-Test-User-ID instead of a Bearer token. Clerk v5 throws without
 * a valid publishable key, which is why the bypass lives at the import
 * boundary rather than as per-component guards.
 */
import React from 'react';
import {
  ClerkProvider as RealClerkProvider,
  useAuth as realUseAuth,
  useUser as realUseUser,
  UserButton as RealUserButton,
  SignIn as RealSignIn,
  SignUp as RealSignUp,
} from '@clerk/clerk-react';
import { TEST_MODE, TEST_USER_ID } from '../testMode';
import { useOptionalTheme } from '../context/ThemeContext';
import { darkColors } from '../theme/colors';

/**
 * Clerk appearance for the current display mode. Light and paperwhite keep the
 * Clerk defaults; dark passes hex variables (Clerk cannot read CSS vars).
 */
export function clerkAppearanceFor(isDark, elements) {
  if (!isDark) return { elements };
  // Popup action rows ('Manage account', 'Sign out') use Clerk's neutral grey,
  // unreadable on navy; force light text and icons. Caller elements win.
  const actionStyle = { color: darkColors.textPrimary };
  return {
    elements: {
      userButtonPopoverActionButton: actionStyle,
      userButtonPopoverActionButtonText: actionStyle,
      userButtonPopoverActionButtonIcon: actionStyle,
      ...elements,
    },
    variables: {
      colorNeutral: darkColors.textPrimary,
      colorBackground: darkColors.bgElevated,
      colorText: darkColors.textPrimary,
      colorTextSecondary: darkColors.textSecondary,
      colorInputBackground: darkColors.bgPrimary,
      colorPrimary: '#f0c866',
    },
  };
}

// Tests that mock ThemeContext may leave useOptionalTheme out; pick the hook
// once at load so it is never called conditionally.
const useThemeIfPresent = typeof useOptionalTheme === 'function' ? useOptionalTheme : () => undefined;

export function useClerkAppearance(elements) {
  // Outside a ThemeProvider the context is undefined: fall back to light.
  const theme = useThemeIfPresent();
  return clerkAppearanceFor(theme?.mode === 'dark', elements);
}

if (TEST_MODE) {
  // eslint-disable-next-line no-console
  console.warn(
    '⚠️⚠️⚠️ BRAINSTORMY FRONTEND TEST-AUTH MODE ACTIVE ⚠️⚠️⚠️\n' +
    `Clerk is bypassed; API requests carry X-Test-User-ID: ${TEST_USER_ID}.\n` +
    'This build must never be deployed. Unset REACT_APP_TEST_MODE to disable.'
  );
}

const testUser = {
  id: TEST_USER_ID,
  firstName: 'Test',
  fullName: 'Test Framework User',
  imageUrl: '',
  primaryEmailAddress: { emailAddress: 'test_framework_e2e@test.brainstormy.local' },
  publicMetadata: {},
};

const mockUseAuth = () => ({
  isLoaded: true,
  isSignedIn: true,
  userId: TEST_USER_ID,
  // client.js never consults the token getter in test mode; null keeps any
  // other caller on its existing "no token" path instead of sending garbage.
  getToken: async () => null,
  signOut: async () => {},
});

const mockUseUser = () => ({ isLoaded: true, isSignedIn: true, user: testUser });

function MockClerkProvider({ children }) {
  return children;
}

function MockUserButton() {
  return (
    <button type="button" aria-label="Account (test mode)" title="Test mode user">
      🧪
    </button>
  );
}

function MockSignIn() {
  return <div>Test-auth mode: sign-in is bypassed.</div>;
}

function MockSignUp() {
  return <div>Test-auth mode: sign-up is bypassed.</div>;
}

export const ClerkProvider = TEST_MODE ? MockClerkProvider : RealClerkProvider;
export const useAuth = TEST_MODE ? mockUseAuth : realUseAuth;
export const useUser = TEST_MODE ? mockUseUser : realUseUser;
export const UserButton = TEST_MODE ? MockUserButton : RealUserButton;
export const SignIn = TEST_MODE ? MockSignIn : RealSignIn;
export const SignUp = TEST_MODE ? MockSignUp : RealSignUp;
